import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { CARS } from '../catalog.js';

const WHEEL_PIVOTS = ['wheel_fl', 'wheel_fr', 'wheel_rl', 'wheel_rr'];

function findNode(root, name) {
  const exact = root.getObjectByName(name);
  if (exact) return exact;
  let match;
  root.traverse(node => {
    if (!match && node.name.startsWith(name)) match = node;
  });
  return match;
}

function prepareMaterials(root) {
  const replacements = new Map();
  const paintMaterial = material => {
    if (!material.isMeshStandardMaterial || material.isMeshPhysicalMaterial || !material.name.toLowerCase().startsWith('paint')) return material;
    if (!replacements.has(material)) {
      const paint = new THREE.MeshPhysicalMaterial();
      // Copy core properties while retaining the physical material's defaults.
      THREE.MeshStandardMaterial.prototype.copy.call(paint, material);
      paint.defines.PHYSICAL = '';
      paint.clearcoat = 1;
      paint.clearcoatRoughness = .08;
      replacements.set(material, paint);
    }
    return replacements.get(material);
  };
  root.traverse(node => {
    if (!node.isMesh) return;
    node.material = Array.isArray(node.material) ? node.material.map(paintMaterial) : paintMaterial(node.material);
    const materials = Array.isArray(node.material) ? node.material : [node.material];
    const glass = materials.some(material => material.name.toLowerCase().includes('glass') || material.transmission > 0 || (material.transparent && material.opacity < 1));
    node.castShadow = !glass;
    node.receiveShadow = !glass;
  });
  // The replacement materials share the original textures; only release material state.
  for (const material of replacements.keys()) material.dispose();
}

function prepareCar(car, scene) {
  const root = new THREE.Group();
  root.name = `gt-${car.id}`;
  root.add(scene);
  root.userData.carId = car.id;
  const authoredRoot = scene.getObjectByName(`${car.id}_licensed_review`);
  root.userData.wheelRadius = authoredRoot?.userData.wheelRadius || .338;
  root.userData.animationTime = null;
  root.userData.wheels = [];
  root.userData.frontWheels = [];
  for (const name of WHEEL_PIVOTS) {
    const pivot = findNode(scene, name);
    const spin = pivot && findNode(pivot, 'wheel_spin');
    if (!pivot || !spin) throw new Error(`Authored car "${car.id}" is missing ${name} or its wheel_spin node.`);
    root.userData.wheels.push(spin);
    if (name === 'wheel_fl' || name === 'wheel_fr') root.userData.frontWheels.push(pivot);
  }
  prepareMaterials(root);
  const brakeLights = new Map();
  root.traverse(node => {
    if (!node.isMesh) return;
    for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
      if (/^lamps_.*red_led/.test(material.name)) brakeLights.set(material, material.emissiveIntensity);
    }
  });
  root.userData.brakeLights = brakeLights;
  return root;
}

export async function loadAuthoredCars(report) {
  const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
  const entries = await Promise.all(CARS.map(async car => {
    // The first four cars are approved; Titan is the final review vehicle.
    const gltf = await loader.loadAsync(`assets/licensed/${car.id}-review.glb`,
      event => report?.(car.id, { loaded: event.loaded, total: event.total }));
    const model = prepareCar(car, gltf.scene);
    report?.(car.id, { decoded: true });
    return [car.id, model];
  }));
  return new Map(entries);
}
